import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

import productsData from "../data/products"; // Ensure this path is correct
import companyLogo from "../assets/images/logoTrans.png";
import "./Navbar.css";

function Navbar() {
  // Get unique categories and subcategories
  const categories = Array.from(new Set(productsData.map((p) => p.category)));

  // Map: category -> unique subcategories
  const subCategoriesMap = {};
  productsData.forEach((p) => {
    if (p.category) {
      if (!subCategoriesMap[p.category]) {
        subCategoriesMap[p.category] = new Set();
      }
      if (p.subCategory) {
        subCategoriesMap[p.category].add(p.subCategory);
      }
    }
  });

  // State for desktop products menu
  const [anchorEl, setAnchorEl] = useState(null); // Anchor for the main "Products" button
  const [desktopMenuOpenCategory, setDesktopMenuOpenCategory] = useState(null); // Tracks open desktop category

  // State for mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerProductsOpen, setDrawerProductsOpen] = useState(false);
  const [drawerSubCategory, setDrawerSubCategory] = useState(null); // Tracks open mobile category

  const navigate = useNavigate();

  // --- Desktop Menu Handlers ---
  const handleProductsMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProductsMenuClose = () => {
    setAnchorEl(null);
    setDesktopMenuOpenCategory(null); // Reset any open category when closing the main menu
  };

  const handleDesktopCategoryClick = (category) => {
    // If a category has subcategories, toggle its expansion.
    // If it doesn't, navigate directly.
    if (subCategoriesMap[category] && subCategoriesMap[category].size > 0) {
      setDesktopMenuOpenCategory(
        desktopMenuOpenCategory === category ? null : category
      );
    } else {
      handleProductNavigation(category); // Navigate if no subcategories
    }
  };

  const handleProductNavigation = (category, subCategory = null) => {
    handleProductsMenuClose(); // Close all desktop menus
    if (subCategory) {
      navigate(
        `/category/${encodeURIComponent(category)}/${encodeURIComponent(
          subCategory
        )}`
      );
    } else {
      navigate(`/category/${encodeURIComponent(category)}`);
    }
  };

  // --- Mobile Drawer Handlers ---
  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
    // Reset mobile product menu state when closing/opening main drawer
    setDrawerProductsOpen(false);
    setDrawerSubCategory(null);
  };

  const handleDrawerProductsToggle = () => {
    setDrawerProductsOpen(!drawerProductsOpen);
    setDrawerSubCategory(null); // Close any open subcategories when toggling main products
  };

  const handleDrawerSubCategoryClick = (category) => {
    setDrawerSubCategory(drawerSubCategory === category ? null : category); // Toggle active subcategory
  };

  const handleMobileNavigation = (category, subCategory = null) => {
    setDrawerOpen(false); // Close the drawer
    if (subCategory) {
      navigate(
        `/category/${encodeURIComponent(category)}/${encodeURIComponent(
          subCategory
        )}`
      );
    } else {
      navigate(`/category/${encodeURIComponent(category)}`);
    }
  };

  // Responsive check (You can use MUI's useMediaQuery hook for more robust responsiveness)
  const isMobile = window.innerWidth < 900;

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        borderBottom: "1.5px solid #b3e0ff",
        background: "#f8fafc",
      }}
    >
      <Toolbar
        sx={{ minHeight: 64, display: "flex", justifyContent: "flex-start" }}
      >
        <RouterLink to="/" className="site-brand" style={{ marginRight: 32 }}>
          <img
            src={companyLogo}
            alt="VICON Products & Services"
            className="site-logo"
          />
        </RouterLink>
        {/* Spacer pushes nav buttons to the right */}
        <div style={{ flex: 1 }} />

        {/* Desktop Navigation */}
        <div
          style={{ display: isMobile ? "none" : "flex", alignItems: "center" }}
        >
          <Button
            color="inherit"
            endIcon={<ArrowDropDownIcon />}
            onClick={handleProductsMenuOpen}
            sx={{
              color: "#1a237e",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1.1rem",
              px: 3,
            }}
            aria-controls={Boolean(anchorEl) ? "products-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl) ? "true" : undefined}
          >
            Products
          </Button>

          {/* Desktop Products Dropdown Menu (Nested like mobile) */}
          <Menu
            id="products-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProductsMenuClose}
            MenuListProps={{
              onMouseLeave: handleProductsMenuClose,
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                width: 250,
              },
            }}
          >
            {categories.map((category) => (
              <React.Fragment key={category}>
                <MenuItem
                  onClick={() => handleDesktopCategoryClick(category)}
                  sx={{
                    justifyContent: "space-between",
                    backgroundColor:
                      desktopMenuOpenCategory === category
                        ? "#e0f7fa"
                        : "transparent",
                    "&:hover": {
                      backgroundColor: "#e0f7fa",
                    },
                    color:
                      desktopMenuOpenCategory === category
                        ? "#1976d2"
                        : "inherit",
                    fontWeight:
                      desktopMenuOpenCategory === category ? 600 : 400,
                    fontSize: "1rem", // Explicitly set category font size
                  }}
                >
                  <ListItemText primary={category} />
                  {subCategoriesMap[category] &&
                    subCategoriesMap[category].size > 0 &&
                    (desktopMenuOpenCategory === category ? (
                      <ArrowDropDownIcon />
                    ) : (
                      <ArrowRightIcon />
                    ))}
                </MenuItem>
                {desktopMenuOpenCategory === category &&
                  subCategoriesMap[category] && (
                    <List component="div" disablePadding sx={{ pl: 2 }}>
                      {[...subCategoriesMap[category]].map((subCategory) => (
                        <MenuItem
                          key={subCategory}
                          onClick={() =>
                            handleProductNavigation(category, subCategory)
                          }
                          sx={{
                            pl: 4, // Indent subcategories
                            fontSize: "0.25rem", // Make subcategory font size smaller
                            minHeight: "auto",
                            paddingTop: "6px",
                            paddingBottom: "6px",
                          }}
                        >
                          <ListItemText primary={subCategory} />
                        </MenuItem>
                      ))}
                    </List>
                  )}
              </React.Fragment>
            ))}
          </Menu>

          {/* Achievers button */}
          <Button
            color="inherit"
            component={RouterLink}
            to="/achievers"
            sx={{
              color: "#1a237e",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1.1rem",
              px: 3,
              ml: 2,
            }}
          >
            Achievers
          </Button>

          {/* About Us and Contact Us buttons */}
          <Button
            color="inherit"
            component={RouterLink}
            to="/aboutus"
            sx={{
              color: "#1a237e",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1.1rem",
              px: 3,
              ml: 2,
            }}
          >
            About Us
          </Button>
          <Button
            color="inherit"
            component={RouterLink}
            to="/contact"
            sx={{
              color: "#1a237e",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1.1rem",
              px: 3,
              ml: 1,
            }}
          >
            Contact Us
          </Button>
        </div>

        {/* Hamburger for Mobile */}
        <div style={{ display: isMobile ? "flex" : "none" }}>
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ ml: 1 }}
          >
            <MenuIcon sx={{ color: "#007CD9", fontSize: 32 }} />
          </IconButton>
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
            PaperProps={{
              sx: { width: 260, background: "#f8fafc" },
            }}
          >
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={handleDrawerProductsToggle}>
                  <ListItemText primary="Products" />
                  {drawerProductsOpen ? (
                    <ArrowDropDownIcon />
                  ) : (
                    <ArrowRightIcon />
                  )}
                </ListItemButton>
              </ListItem>
              {drawerProductsOpen && (
                <List component="div" disablePadding sx={{ pl: 2 }}>
                  {categories.map((category) => (
                    <React.Fragment key={category}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => {
                            // If a category has subcategories, toggle expansion
                            if (
                              subCategoriesMap[category] &&
                              subCategoriesMap[category].size > 0
                            ) {
                              handleDrawerSubCategoryClick(category);
                            } else {
                              // Otherwise, navigate directly to the category page
                              handleMobileNavigation(category);
                            }
                          }}
                          sx={{
                            backgroundColor:
                              drawerSubCategory === category
                                ? "#e0f7fa"
                                : "transparent",
                            "&:hover": {
                              backgroundColor: "#e0f7fa",
                            },
                            color:
                              drawerSubCategory === category
                                ? "#1976d2"
                                : "inherit",
                            fontWeight:
                              drawerSubCategory === category ? 600 : 400,
                            fontSize: "1rem", // Explicitly set category font size
                          }}
                        >
                          <ListItemText primary={category} />
                          {subCategoriesMap[category] &&
                            subCategoriesMap[category].size > 0 &&
                            (drawerSubCategory === category ? (
                              <ArrowDropDownIcon />
                            ) : (
                              <ArrowRightIcon />
                            ))}
                        </ListItemButton>
                      </ListItem>
                      {drawerSubCategory === category &&
                        subCategoriesMap[category] && (
                          <List component="div" disablePadding sx={{ pl: 4 }}>
                            {[...subCategoriesMap[category]].map(
                              (subCategory) => (
                                <ListItem key={subCategory} disablePadding>
                                  <ListItemButton
                                    onClick={() =>
                                      handleMobileNavigation(
                                        category,
                                        subCategory
                                      )
                                    }
                                    sx={{
                                      pl: 7, // Further indent for subcategories
                                      fontSize: "0.85rem", // Make subcategory font size smaller
                                      minHeight: "auto",
                                      paddingTop: "6px",
                                      paddingBottom: "6px",
                                    }}
                                  >
                                    <ListItemText primary={subCategory} />
                                  </ListItemButton>
                                </ListItem>
                              )
                            )}
                          </List>
                        )}
                    </React.Fragment>
                  ))}
                </List>
              )}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    toggleDrawer(false)();
                    navigate("/achievers");
                  }}
                >
                  <ListItemText primary="Achievers" />
                </ListItemButton>
              </ListItem>
              <Divider sx={{ my: 1 }} />
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    toggleDrawer(false)();
                    navigate("/aboutus");
                  }}
                >
                  <ListItemText primary="About Us" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    toggleDrawer(false)();
                    navigate("/contact");
                  }}
                >
                  <ListItemText primary="Contact Us" />
                </ListItemButton>
              </ListItem>
            </List>
          </Drawer>
        </div>
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;
